// src/pages/InvoiceDetail.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../api";
import { fetchAccess } from "../access";
import { buildDocumentTimestamp } from "../utils/documentTimestamp";
import { ui, uiHandlers } from "../uiStyles";

function money(x) {
  if (x == null) return "$0.00";
  const n = typeof x === "string" ? Number(x) : x;
  return Number(n || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return String(d || "—");
  }
}

function dateToInputValue(d) {
  if (!d) return "";
  const s = String(d);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

export default function InvoiceDetail() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();

  const [inv, setInv] = useState(null);
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [err, setErr] = useState("");
  const [access, setAccess] = useState({ can_send_email: true, can_move_money: true });
  const [loading, setLoading] = useState(true);

  const [newDesc, setNewDesc] = useState("");
  const [newQty, setNewQty] = useState("1.00");
  const [newUnit, setNewUnit] = useState("0.00");
  const [newTax, setNewTax] = useState("0.0000");
  const [itemError, setItemError] = useState("");
  const [savingItem, setSavingItem] = useState(false);
  const [libraryItems, setLibraryItems] = useState([]);
  const [selectedLibraryItemId, setSelectedLibraryItemId] = useState("");
  const [saveManualLineItem, setSaveManualLineItem] = useState(false);

  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("CHECK");
  const [payRef, setPayRef] = useState("");
  const [payDate, setPayDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [savingPay, setSavingPay] = useState(false);
  const [payOnlineBusy, setPayOnlineBusy] = useState(false);

  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailAction, setEmailAction] = useState(null);

  const [dueDate, setDueDate] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");
  const [extraExclusions, setExtraExclusions] = useState("");
  const [extraConditions, setExtraConditions] = useState("");
  const [paymentSchedule, setPaymentSchedule] = useState([]);
  const [documentPaymentNote, setDocumentPaymentNote] = useState("");
  const [documentTerms, setDocumentTerms] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);
  const [metaMsg, setMetaMsg] = useState("");

  async function loadLibraryItems() {
    try {
      const data = await apiRequest("/products-services/");
      setLibraryItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load Products & Services library", e);
      setLibraryItems([]);
    }
  }

  function applyLibraryItem(itemId) {
    const selected = libraryItems.find(
      (item) => String(item.id) === String(itemId)
    );

    setSelectedLibraryItemId(itemId);

    if (!selected) return;

    setNewDesc(selected.description || selected.name || "");
    setNewQty(String(selected.default_quantity || "1.00"));
    setNewUnit(String(selected.default_rate || "0.00"));
    setNewTax(selected.taxable ? "7.75" : "0");
  }

  async function load() {
    setLoading(true);
    setErr("");
    setMetaMsg("");

    try {
      const data = await apiRequest(`/invoices/${invoiceId}`);

      let client = null;
      if (data?.client_id) {
        try {
          client = await apiRequest(`/clients/${data.client_id}`);
        } catch {
          client = null;
        }
      }

      setInv({ ...data, client });
      setItems(Array.isArray(data?.items) ? data.items : []);

      setDueDate(dateToInputValue(data?.due_date));
      setCustomerNotes(data?.customer_notes || "");
      setExtraExclusions(
        Array.isArray(data?.extra_exclusions) ? data.extra_exclusions.join("\n") : ""
      );
      setExtraConditions(
        Array.isArray(data?.extra_conditions) ? data.extra_conditions.join("\n") : ""
      );
      setPaymentSchedule(
        Array.isArray(data?.payment_schedule) ? data.payment_schedule : []
      );
      setDocumentPaymentNote(data?.document_payment_note || "");
      setDocumentTerms(data?.document_terms || "");

      if (data?.client_id) {
        const all = await apiRequest(`/clients/${data.client_id}/payments`);
        const paymentList = Array.isArray(all) ? all : [];
        setPayments(
          paymentList.filter(
            (p) => Number(p.invoice_id) === Number(invoiceId)
          )
        );
      } else {
        setPayments([]);
      }
    } catch (e) {
      setErr(String(e?.message || e || "Failed to load."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAccess().then(setAccess);
  }, []);

  useEffect(() => {
    load();
    loadLibraryItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  async function addItem() {
    if (!newDesc.trim()) return;

    const taxPercent = Number(newTax);
    if (!Number.isFinite(taxPercent) || taxPercent < 0 || taxPercent > 100) {
      setItemError(
        "Tax must be a percentage from 0 to 100 (example: 9.75 = 9.75%)."
      );
      return;
    }

    setSavingItem(true);
    setItemError("");
    setErr("");

    try {
      const payload = {
        description: newDesc.trim(),
        qty: Number(newQty || "0"),
        unit_price: Number(newUnit || "0"),
        tax_rate: taxPercent / 100,
      };

      await apiRequest(`/invoices/${invoiceId}/items`, {
        method: "POST",
        body: payload,
      });

      if (!selectedLibraryItemId && saveManualLineItem) {
        await apiRequest("/products-services/", {
          method: "POST",
          body: {
            item_type: "service",
            name: newDesc.trim(),
            description: newDesc.trim(),
            unit: null,
            default_quantity: Number(newQty || "1"),
            default_rate: Number(newUnit || "0"),
            cost: 0,
            taxable: Number(newTax || "0") > 0,
            sku: null,
            active: true,
          },
        });

        await loadLibraryItems();
      }

      setNewDesc("");
      setNewQty("1.00");
      setNewUnit("0.00");
      setNewTax("0.0000");
      setSelectedLibraryItemId("");
      setSaveManualLineItem(false);
      await load();
    } catch (e) {
      setItemError(String(e?.message || e || "Add item failed."));
    } finally {
      setSavingItem(false);
    }
  }

  async function deleteItem(id) {
    if (!window.confirm("Delete this line?")) return;

    setErr("");

    try {
      await apiRequest(`/invoices/${invoiceId}/items/${id}`, {
        method: "DELETE",
      });
      await load();
    } catch (e) {
      setErr(String(e?.message || e || "Delete failed."));
    }
  }


  async function startOnlinePayment() {
    const gate = await fetchAccess();
    if (!gate.can_move_money) {
      setErr("Payments are limited to the owner and assistants.");
      return;
    }

    setPayOnlineBusy(true);
    setErr("");

    try {
      const result = await apiRequest(`/billing/invoices/${invoiceId}/pay-online`, {
        method: "POST",
      });

      if (!result?.checkout_url) {
        throw new Error("Stripe checkout URL was not returned.");
      }

      window.location.href = result.checkout_url;
    } catch (e) {
      setErr(String(e?.message || e || "Could not start online payment."));
    } finally {
      setPayOnlineBusy(false);
    }
  }

  async function submitPayment() {
    const gate = await fetchAccess();
    if (!gate.can_move_money) {
      setErr("Payments are limited to the owner and assistants.");
      return;
    }

    if (!payAmount || Number(payAmount) <= 0) {
      alert("Enter an amount greater than 0.");
      return;
    }

    if (!inv?.client_id) {
      setErr("Missing client on invoice.");
      return;
    }

    setSavingPay(true);
    setErr("");

    try {
      const payload = {
        client_id: inv.client_id,
        invoice_id: Number(invoiceId),
        amount: Number(payAmount),
        method: payMethod,
        reference: payRef || null,
        received_at: `${payDate}T12:00:00Z`,
        notes: null,
      };

      await apiRequest(`/payments/`, {
        method: "POST",
        body: payload,
      });

      setPayOpen(false);
      setPayAmount("");
      setPayRef("");
      await load();
    } catch (e) {
      setErr(String(e?.message || e || "Payment failed."));
    } finally {
      setSavingPay(false);
    }
  }

  function linesFromText(value) {
    return value
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }

  function updatePaymentMilestone(index, field, value) {
    setPaymentSchedule((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    );
  }

  function addPaymentMilestone() {
    setPaymentSchedule((current) => [
      ...current,
      { label: "", percent: 0, due_when: "" },
    ]);
  }

  function removePaymentMilestone(index) {
    setPaymentSchedule((current) =>
      current.filter((_, itemIndex) => itemIndex !== index)
    );
  }

  async function saveInvoiceMeta() {
    setSavingMeta(true);
    setErr("");
    setMetaMsg("");

    try {
      const payload = {
        due_date: dueDate ? dueDate : null,
        customer_notes:
          customerNotes && customerNotes.trim()
            ? customerNotes.trim()
            : null,
        extra_exclusions: linesFromText(extraExclusions),
        extra_conditions: linesFromText(extraConditions),
        payment_schedule: paymentSchedule
          .map((item) => ({
            label: String(item?.label || "").trim(),
            percent: Number(item?.percent || 0),
            due_when: String(item?.due_when || "").trim(),
          }))
          .filter(
            (item) =>
              item.label || item.due_when || Number(item.percent) !== 0
          ),
        document_payment_note: documentPaymentNote.trim(),
        document_terms: documentTerms.trim(),
      };

      await apiRequest(`/invoices/${invoiceId}`, {
        method: "PATCH",
        body: payload,
      });

      setMetaMsg("Saved.");
      await load();
    } catch (e) {
      setErr(String(e?.message || e || "Failed to save."));
    } finally {
      setSavingMeta(false);
      setTimeout(() => setMetaMsg(""), 2500);
    }
  }


  async function saveCommunicationNote() {
    if (!emailAction?.jobId) {
      setEmailError("This invoice is not linked to a project/job yet.");
      return;
    }

    try {
      const now = new Date().toLocaleString();
      const me = await apiRequest("/auth/me");
      const senderEmail = me?.email || "unknown user";

      const note = `[CLIENT COMMUNICATION]

Document Type: ${emailAction.documentType}
Document Number: ${emailAction.documentNumber}

PDF File:
${emailAction.fileName}

Recipient:
${emailAction.clientEmail}

Date/Time:
${now}

Sent By:
${senderEmail}

Subject:
${emailAction.subject}

Delivery Method:
Tenant Email Client

Status:
Marked sent by user`;

      await apiRequest(
        `/notes/job/${emailAction.jobId}`,
        {
          method: "POST",
          body: {
            content: note,
          },
        }
      );

      // Structured reporting ledger
      await apiRequest(
        "/document-activity",
        {
          method: "POST",
          body: {
            job_id: emailAction.jobId,
            client_id: inv?.client_id || null,

            document_type: "INVOICE",
            document_id: Number(invoiceId),
            document_number: emailAction.documentNumber,

            activity_type: "EMAILED",

            amount: inv?.total || null,

            email_to: emailAction.clientEmail,
            subject: emailAction.subject,
            file_name: emailAction.fileName,

            notes: "Invoice emailed through tenant email client",
          },
        }
      );

      setEmailSuccess("Communication note + ledger activity saved.");
      setEmailAction(null);
    } catch (e) {
      console.error(e);
      setEmailError(
        e?.message || "Failed to save communication note."
      );
    }
  }

  async function sendInvoiceEmail() {
    const gate = await fetchAccess();
    if (!gate.can_send_email) {
      setEmailError("Email is limited to the owner.");
      return;
    }

    setEmailSending(true);
    setEmailError("");
    setEmailSuccess("");
    setEmailAction(null);

    try {
      const clientEmail =
        inv?.client?.email ||
        "";

      const clientName =
        inv?.client?.company_name ||
        inv?.client?.contact_name ||
        inv?.client?.name ||
        "Client";

      if (!clientEmail) {
        setEmailError(
          "Client email is missing. Add a client email before preparing this invoice email."
        );
        return;
      }

      const invoiceNumber = inv.invoice_number || invoiceId;
      const timestamp = new Date()
        .toISOString()
        .replace(/[-:]/g, "")
        .replace("T", "-")
        .slice(0, 15);

      const jobPart = inv?.job_id ? `-JOB${inv.job_id}` : "";

      const pdfFileName =
        `Invoice-${invoiceNumber}${jobPart}-${timestamp}.pdf`;

      const printUrl = `/invoices/${invoiceId}/print`;
      window.open(printUrl, "_blank", "noopener,noreferrer");

      const subject = `Invoice ${invoiceNumber}`;

      const body = `Hi ${clientName},

I hope you’re doing well.

Please see the attached invoice PDF for your records.

If you have any questions regarding this invoice or payment, please let us know.

Thank you,

`;

      const mailto =
        `mailto:${encodeURIComponent(clientEmail)}` +
        `?subject=${encodeURIComponent(subject)}` +
        `&body=${encodeURIComponent(body)}`;

      window.location.href = mailto;

      // ========================================================
      // OET V1 — Operational Event Telemetry
      // --------------------------------------------------------
      // Tracks invoice email preparation activity.
      // This becomes part of tenant operational telemetry.
      // ========================================================
      try {
        await fetch("/api/document-activity", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "x-tenant": localStorage.getItem("x-tenant") || "dfasio",
          },
          body: JSON.stringify({
            job_id: inv?.job_id || null,
            client_id: inv?.client_id || null,
            document_type: "invoice",
            document_id: inv?.id || null,
            document_number: invoiceNumber,
            activity_type: "EMAIL_PREPARED",
            email_to: clientEmail,
            subject,
            file_name: pdfFileName,
            notes: "Invoice Prepare Email triggered from Invoice Detail page.",
          }),
        });
      } catch (telemetryError) {
        console.error("OET telemetry error:", telemetryError);
      }

      setEmailAction({
        fileName: pdfFileName,
        clientEmail,
        subject,
        jobId: inv?.job_id,
        documentType: "Invoice",
        documentNumber: invoiceNumber,
      });

      setEmailSuccess(
        "Print/Save PDF page opened. Save the invoice as a PDF, then attach it to the email draft before sending."
      );
    } catch (e) {
      console.error("Invoice email error:", e);
      setEmailError(
        String(e?.message || e || "Failed to prepare invoice email.")
      );
    } finally {
      setEmailSending(false);
    }
  }

  if (loading) {
    return <div style={{ padding: 24 }}>Loading…</div>;
  }

  if (err) {
    return (
      <div style={{ padding: 24 }}>
        <p style={{ color: "crimson" }}>{err}</p>
        <p>
          <button onClick={load}>Retry</button>{" "}
          <button onClick={() => navigate(-1)}>Back</button>
        </p>
      </div>
    );
  }

  if (!inv) return null;

  const totals = {
    subtotal: money(inv.subtotal),
    tax: money(inv.tax_total),
    total: money(inv.total),
    paid: money(inv.amount_paid),
    balance: money(
      (Number(inv.total || 0) - Number(inv.amount_paid || 0)) || 0
    ),
  };

  // Headquarters UI System V1 aliases.
  // These keep the invoice logic stable while moving the page onto shared styling.
  const page = ui.page;
  const card = ui.card;
  const subCard = ui.subCard;
  const darkInput = ui.input;
  const cell = ui.tableTd;
  const tableTh = ui.tableTh;
  const primaryBtn = ui.btnPrimary;
  const secondaryBtn = ui.btnSecondary;
  const btnDanger = ui.btnDanger;
  const linkStyle = ui.link;

  const focusHandlers = uiHandlers.focus;
  const primaryHoverHandlers = uiHandlers.primaryHover;
  const lineBtn = {
    width: 128,
    height: 36,
    minWidth: 128,
    maxWidth: 128,
    minHeight: 36,
    maxHeight: 36,
    padding: 0,
    borderRadius: 8,
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    boxShadow: "none",
  };
  const removeBtn = {
    ...lineBtn,
    background: "transparent",
    color: "#ef4444",
    border: "1px solid #ef4444",
  };
  const addBtn = {
    ...lineBtn,
    background: "#f5c542",
    color: "#0b1b2b",
    border: "1px solid #f5c542",
  };
  const secondaryHoverHandlers = uiHandlers.secondaryHover;

  return (
    <div style={page}>
      <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={() => navigate(-1)} style={secondaryBtn}>
          ← Back
        </button>

        <Link
          to="/invoices"
          style={linkStyle}
        >
          Invoices
        </Link>
      </div>

      <div
        style={{
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 18,
          padding: 18,
          background: "#081a2b",
        }}
      >
        <div
          style={{
            background: "#10263a",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 16,
            padding: 18,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div>
              <h3 style={{ margin: "0 0 6px", color: "#ffffff" }}>
                Invoice {inv.invoice_number || `#${invoiceId}`}
              </h3>

              <div
                style={{
                  color: "#9fb0c3",
                  fontSize: 14,
                  display: "grid",
                  gridTemplateColumns: "90px 1fr",
                  rowGap: 10,
                  columnGap: 10,
                  alignItems: "center",
                }}
              >
                <span>Issue Date:</span>
                <span style={{ color: "#ffffff" }}>{fmtDate(inv.issue_date)}</span>

                <span>Due Date:</span>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    type="date"
                    value={dueDate || ""}
                    onChange={(e) => setDueDate(e.target.value)}
                    style={darkInput}
                  />

                  <button
                    className="no-print"
                    onClick={saveInvoiceMeta}
                    disabled={savingMeta}
                    style={{
                      ...primaryBtn,
                      padding: "8px 12px",
                      opacity: savingMeta ? 0.7 : 1,
                    }}
                    title="Saves the invoice and Document Station fields"
                  >
                    {savingMeta ? "Saving..." : "Save"}
                  </button>

                  {metaMsg && (
                    <span style={{ color: "#7ddf8a", fontSize: 13 }}>
                      {metaMsg}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Link
                className="no-print"
                to={`/invoices/${invoiceId}/print`}
                style={{
                  ...secondaryBtn,
                  display: "inline-block",
                  textDecoration: "none",
                }}
              >
                Print
              </Link>

              {access.can_send_email && (
              <button
                className="no-print"
                onClick={sendInvoiceEmail}
                disabled={emailSending}
                style={{
                  ...secondaryBtn,
                  opacity: emailSending ? 0.7 : 1,
                }}
              >
                {emailSending ? "Preparing…" : "Prepare Email"}
              </button>
              )}


              {access.can_move_money && (
              <button
                className="no-print"
                onClick={startOnlinePayment}
                disabled={payOnlineBusy}
                style={{
                  ...primaryBtn,
                  background: "#22c55e",
                  opacity: payOnlineBusy ? 0.7 : 1,
                }}
              >
                {payOnlineBusy ? "Opening…" : "Pay Online"}
              </button>
              )}

              {access.can_move_money && (
              <button
                className="no-print"
                onClick={() => setPayOpen(true)}
                style={primaryBtn}
              >
                Receive Payment
              </button>
              )}
            </div>
          </div>

          {emailAction && (
            <div
              className="no-print"
              style={{
                marginTop: 12,
                background: "rgba(224,184,79,0.12)",
                border: "1px solid rgba(224,184,79,0.45)",
                color: "#f7e7b2",
                padding: "12px 14px",
                borderRadius: 10,
                lineHeight: 1.5,
              }}
            >
              <div style={{ fontWeight: 800, marginBottom: 6 }}>
                Take Action: Attach the Invoice PDF
              </div>

              <div style={{ fontSize: 14 }}>
                Your invoice print page should now be open. Save it as a PDF, then attach it to your email draft:
              </div>

              <div
                style={{
                  marginTop: 8,
                  padding: "8px 10px",
                  background: "rgba(0,0,0,0.18)",
                  borderRadius: 8,
                  fontFamily: "monospace",
                  fontSize: 13,
                  color: "#ffffff",
                }}
              >
                {emailAction.fileName}
              </div>

              <div style={{ marginTop: 8, fontSize: 13, color: "#d7e7f5" }}>
                Most browsers save it in your Downloads folder unless you chose another location.
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                <button
                  type="button"
                  onClick={saveCommunicationNote}
                  style={{
                    padding: "7px 10px",
                    background: "#1d4ed8",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  Mark Sent & Save Job Note
                </button>
              </div>
            </div>
          )}

          {(emailSuccess || emailError) && (
            <div style={{ marginTop: 10, fontSize: 13 }}>
              {emailSuccess && <div style={{ color: "#7ddf8a" }}>{emailSuccess}</div>}
              {emailError && <div style={{ color: "#ff8a8a" }}>{emailError}</div>}
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: 12,
            padding: 16,
            borderRadius: 14,
            background: "#10263a",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          {itemError && (
            <div
              role="alert"
              style={{
                background: "rgba(255,138,138,0.10)",
                border: "1px solid rgba(255,138,138,0.30)",
                color: "#ff8a8a",
                padding: "10px 12px",
                borderRadius: 10,
                marginBottom: 14,
              }}
            >
              {itemError}
            </div>
          )}


        <style>{`
          .jt-doc-lines { display: flex; flex-direction: column; gap: 8px; }
          .jt-doc-line {
            display: grid;
            grid-template-columns: minmax(0, 1fr) 100px 130px 110px 120px 140px;
            gap: 10px;
            align-items: center;
          }
          .jt-doc-line > * { min-width: 0; }
          .jt-doc-head {
            color: #ffffff;
            font-weight: 700;
            font-size: 14px;
            background: #0f2235;
            border-radius: 10px;
            padding: 10px 0;
          }
          .jt-doc-line > div { padding-left: 12px; }
          .jt-doc-line .num { text-align: left; }
          .jt-doc-line input {
            width: 100% !important;
            max-width: none !important;
            min-width: 0 !important;
            box-sizing: border-box;
            text-align: left !important;
          }
        `}</style>

          <div className="jt-doc-lines">
            <div className="jt-doc-line jt-doc-head">
              <div>Description</div>
              <div className="num">Qty</div>
              <div className="num">Unit Price</div>
              <div className="num">Tax</div>
              <div className="num">Line Total</div>
              <div>Actions</div>
            </div>
            {items.map((it) => (
              <div className="jt-doc-line" key={it.id}>
                <div style={{ color: "#ffffff", overflowWrap: "anywhere" }}>{it.description}</div>
                <div className="num" style={{ color: "#ffffff" }}>{Number(it.qty || 0).toFixed(2)}</div>
                <div className="num" style={{ color: "#ffffff" }}>{money(it.unit_price)}</div>
                <div className="num" style={{ color: "#ffffff" }}>{(Number(it.tax_rate || 0) * 100).toFixed(2)}%</div>
                <div className="num" style={{ color: "#ffffff" }}>{money(it.line_total)}</div>
                <button type="button" onClick={() => deleteItem(it.id)} style={removeBtn}>Remove</button>
              </div>
            ))}
            <div className="jt-doc-line no-print">
              <input
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Description"
                style={darkInput}
                {...focusHandlers}
              />
              <input
                value={newQty}
                onChange={(e) => setNewQty(e.target.value)}
                placeholder="Qty"
                aria-label="Quantity"
                className="num"
                style={darkInput}
                {...focusHandlers}
              />
              <input
                value={newUnit}
                onChange={(e) => setNewUnit(e.target.value)}
                placeholder="Price"
                aria-label="Unit price"
                className="num"
                style={darkInput}
                {...focusHandlers}
              />
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={newTax}
                onChange={(e) => setNewTax(e.target.value)}
                placeholder="Tax %"
                aria-label="Tax percentage, for example 9.75 equals 9.75 percent"
                className="num"
                style={darkInput}
                {...focusHandlers}
              />
              <div className="num" style={{ color: "#9fb0c3" }}>
                {money((Number(newQty) || 0) * (Number(newUnit) || 0))}
              </div>
              <button
                type="button"
                onClick={addItem}
                disabled={savingItem || !newDesc.trim()}
                style={{ ...addBtn, opacity: savingItem || !newDesc.trim() ? 0.7 : 1 }}
              >
                Add Item
              </button>
            </div>
          </div>

          <div className="no-print" style={{ marginTop: 12, maxWidth: 380 }}>
            <select
              value={selectedLibraryItemId}
              onChange={(e) => applyLibraryItem(e.target.value)}
              style={darkInput}
              {...focusHandlers}
            >
              <option value="">Select Line Item</option>
              {libraryItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                  {item.sku ? ` — ${item.sku}` : ""}
                  {item.default_rate != null ? ` — ${money(item.default_rate)}` : ""}
                </option>
              ))}
            </select>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#9fb0c3", marginTop: 8 }}>
              <input
                type="checkbox"
                checked={saveManualLineItem}
                onChange={(e) => setSaveManualLineItem(e.target.checked)}
                style={{ width: 16, height: 16 }}
              />
              Save Line Item
            </label>
          </div>

        </div>

        <div
          style={{
            marginTop: 16,
            padding: 16,
            borderRadius: 14,
            background: "#10263a",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8, color: "#ffffff" }}>
            Customer Notes
          </div>

          <textarea
            value={customerNotes}
            onChange={(e) => setCustomerNotes(e.target.value)}
            placeholder="Notes specific to this customer or invoice"
            style={{
              width: "100%",
              minHeight: 90,
              padding: 12,
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.10)",
              background: "#0f2235",
              color: "#ffffff",
              fontSize: 14,
              resize: "vertical",
              boxSizing: "border-box",
            }}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginTop: 8,
              flexWrap: "wrap",
            }}
          >
            <button
              className="no-print"
              onClick={saveInvoiceMeta}
              disabled={savingMeta}
              style={{
                ...primaryBtn,
                opacity: savingMeta ? 0.7 : 1,
              }}
            >
              {savingMeta ? "Saving…" : "Save Notes"}
            </button>
          </div>
        </div>

        <div
          style={{
            marginTop: 16,
            padding: 16,
            borderRadius: 14,
            background: "#10263a",
            border: "1px solid rgba(224,184,79,0.38)",
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 18, color: "#ffffff" }}>
            Document Station
          </div>
          <div style={{ marginTop: 4, marginBottom: 16, color: "#9fb0c3", fontSize: 13 }}>
            These invoice-specific terms are preserved even if tenant defaults change later.
          </div>

          <div style={{ display: "grid", gap: 16 }}>
            <label>
              <div style={{ marginBottom: 6, color: "#dbe7f3", fontWeight: 700 }}>
                Exclusions — one per line
              </div>
              <textarea
                value={extraExclusions}
                onChange={(e) => setExtraExclusions(e.target.value)}
                placeholder="Work or materials not included in this invoice"
                style={{ ...darkInput, width: "100%", minHeight: 82, boxSizing: "border-box" }}
                {...focusHandlers}
              />
            </label>

            <label>
              <div style={{ marginBottom: 6, color: "#dbe7f3", fontWeight: 700 }}>
                Conditions — one per line
              </div>
              <textarea
                value={extraConditions}
                onChange={(e) => setExtraConditions(e.target.value)}
                placeholder="Conditions that apply to this invoice"
                style={{ ...darkInput, width: "100%", minHeight: 82, boxSizing: "border-box" }}
                {...focusHandlers}
              />
            </label>

            <div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 8 }}>
                <div style={{ color: "#dbe7f3", fontWeight: 700 }}>
                  Payment Schedule
                </div>
                <button
                  type="button"
                  className="no-print"
                  onClick={addPaymentMilestone}
                  style={{ ...secondaryBtn, padding: "7px 10px" }}
                >
                  + Add Payment
                </button>
              </div>

              {paymentSchedule.length === 0 ? (
                <div style={{ color: "#9fb0c3", fontSize: 13 }}>
                  No payment milestones. Use “Add Payment” if this invoice needs them.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  {paymentSchedule.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(140px, 1fr) 100px minmax(190px, 1.5fr) auto",
                        gap: 8,
                        alignItems: "center",
                      }}
                    >
                      <input
                        value={item?.label || ""}
                        onChange={(e) => updatePaymentMilestone(index, "label", e.target.value)}
                        placeholder="Deposit"
                        aria-label={`Payment ${index + 1} label`}
                        style={darkInput}
                        {...focusHandlers}
                      />
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={item?.percent ?? 0}
                        onChange={(e) => updatePaymentMilestone(index, "percent", e.target.value)}
                        aria-label={`Payment ${index + 1} percent`}
                        style={darkInput}
                        {...focusHandlers}
                      />
                      <input
                        value={item?.due_when || ""}
                        onChange={(e) => updatePaymentMilestone(index, "due_when", e.target.value)}
                        placeholder="When this payment is due"
                        aria-label={`Payment ${index + 1} due condition`}
                        style={darkInput}
                        {...focusHandlers}
                      />
                      <button
                        type="button"
                        className="no-print"
                        onClick={() => removePaymentMilestone(index)}
                        style={{ ...btnDanger, padding: "7px 10px" }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label>
              <div style={{ marginBottom: 6, color: "#dbe7f3", fontWeight: 700 }}>
                Payment Instructions
              </div>
              <textarea
                value={documentPaymentNote}
                onChange={(e) => setDocumentPaymentNote(e.target.value)}
                placeholder="Example: Pay by Zelle to admin@dfasio.com"
                style={{ ...darkInput, width: "100%", minHeight: 72, boxSizing: "border-box" }}
                {...focusHandlers}
              />
            </label>

            <label>
              <div style={{ marginBottom: 6, color: "#dbe7f3", fontWeight: 700 }}>
                Terms
              </div>
              <textarea
                value={documentTerms}
                onChange={(e) => setDocumentTerms(e.target.value)}
                placeholder="Terms that apply to this invoice"
                style={{ ...darkInput, width: "100%", minHeight: 110, boxSizing: "border-box" }}
                {...focusHandlers}
              />
            </label>

            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button
                type="button"
                className="no-print"
                onClick={saveInvoiceMeta}
                disabled={savingMeta}
                style={{ ...primaryBtn, opacity: savingMeta ? 0.7 : 1 }}
                {...primaryHoverHandlers}
              >
                {savingMeta ? "Saving…" : "Save Document"}
              </button>
              {metaMsg && <span style={{ color: "#7ddf8a", fontSize: 13 }}>{metaMsg}</span>}
            </div>
          </div>
        </div>

        <div
          style={{
            marginTop: 18,
            padding: 16,
            borderRadius: 14,
            background: "#10263a",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 10, color: "#ffffff" }}>
            Payments
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#0f2235" }}>
                <th style={{ ...cell, textAlign: "left", color: "#ffffff" }}>Date</th>
                <th style={{ ...cell, textAlign: "left", color: "#ffffff" }}>Method</th>
                <th style={{ ...cell, textAlign: "left", color: "#ffffff" }}>Reference</th>
                <th style={{ ...cell, textAlign: "right", color: "#ffffff" }}>Amount</th>
                <th style={{ ...cell, width: 120, color: "#ffffff" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr>
                  <td
                    style={{
                      ...cell,
                      color: "#9fb0c3",
                    }}
                    colSpan={5}
                  >
                    — No payments yet —
                  </td>
                </tr>
              ) : (
                payments.map((p) => (
                  <tr key={p.id}>
                    <td style={{ ...cell, color: "#ffffff" }}>
                      {fmtDate(p.received_at)}
                    </td>
                    <td style={{ ...cell, color: "#ffffff" }}>
                      {p.method || "—"}
                    </td>
                    <td style={{ ...cell, color: "#ffffff" }}>
                      {p.reference || "—"}
                    </td>
                    <td
                      style={{
                        ...cell,
                        textAlign: "right",
                        color: "#ffffff",
                      }}
                    >
                      {money(p.amount)}
                    </td>
                    <td style={cell}>
                      <Link
                        className="no-print"
                        to={`/invoices/${invoiceId}/payments/${p.id}/receipt`}
                        style={{ color: "#00c2ff", textDecoration: "none", fontWeight: 600 }}
                      >
                        Receipt
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 220px",
            marginTop: 16,
          }}
        >
          <div />
          <div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 6,
                color: "#ffffff",
              }}
            >
              <div>Subtotal</div>
              <div>{totals.subtotal}</div>
              <div>Tax</div>
              <div>{totals.tax}</div>
              <div style={{ fontWeight: 700 }}>Total</div>
              <div style={{ fontWeight: 700 }}>{totals.total}</div>
              <div>Paid</div>
              <div>{totals.paid}</div>
              <div style={{ fontWeight: 700 }}>Balance</div>
              <div style={{ fontWeight: 700 }}>{totals.balance}</div>
            </div>
          </div>
        </div>
      </div>

      {payOpen && (
        <div
          className="no-print"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            zIndex: 50,
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 480,
              background: "#10263a",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#ffffff",
              borderRadius: 16,
              padding: 18,
              boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 14 }}>Receive Payment</h3>

            <div style={{ display: "grid", gap: 12 }}>
              <label>
                <div style={{ marginBottom: 4, color: "#9fb0c3" }}>Amount</div>
                <input
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  placeholder="0.00"
                  style={{ ...darkInput, width: "100%", boxSizing: "border-box" }}
                />
              </label>

              <label>
                <div style={{ marginBottom: 4, color: "#9fb0c3" }}>Method</div>
                <select
                  value={payMethod}
                  onChange={(e) => setPayMethod(e.target.value)}
                  style={{ ...darkInput, width: "100%" }}
                >
                  <option value="CHECK">Check</option>
                  <option value="ACH">ACH</option>
                  <option value="CARD">Card</option>
                  <option value="CASH">Cash</option>
                  <option value="ZELLE">Zelle</option>
                  <option value="WIRE">Wire</option>
                </select>
              </label>

              <label>
                <div style={{ marginBottom: 4, color: "#9fb0c3" }}>Reference</div>
                <input
                  value={payRef}
                  onChange={(e) => setPayRef(e.target.value)}
                  placeholder="Check #, confirmation #, etc."
                  style={{ ...darkInput, width: "100%", boxSizing: "border-box" }}
                />
              </label>

              <label>
                <div style={{ marginBottom: 4, color: "#9fb0c3" }}>Date</div>
                <input
                  type="date"
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                  style={{ ...darkInput, width: "100%", boxSizing: "border-box" }}
                />
              </label>
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "flex-end",
                marginTop: 16,
              }}
            >
              <button
                onClick={() => setPayOpen(false)}
                style={secondaryBtn}
              >
                Cancel
              </button>
              <button
                onClick={submitPayment}
                disabled={savingPay}
                style={{
                  ...primaryBtn,
                  opacity: savingPay ? 0.7 : 1,
                }}
              >
                {savingPay ? "Saving…" : "Save Payment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
